import React from 'react'
import { ArrowDownRight, ArrowUpRight, Info, LucideIcon, Minus, X } from 'lucide-react'

export type KPIColor = 'green' | 'red' | 'blue' | 'amber' | 'purple'

export interface KPIInfo {
  /** Plain-language definition of the metric (what it measures). */
  definition: string
  /** Mathematical formula. Rendered in a monospace block. */
  formula?: string
  /** Extra calculation notes: data sources, SQL logic, filters, edge cases. */
  calculation?: string
  /** Underlying data source (tables / endpoints). */
  source?: string
}

export interface KPICardData {
  id: string
  label: string
  value: number | string
  detail?: string
  icon: LucideIcon
  color: KPIColor
  /** Optional previous-period value; when paired with `value` a delta chip is shown. */
  previousValue?: number | null
  /** Pre-computed delta percentage (takes precedence over auto-computed one). */
  deltaPct?: number | null
  /** Label shown next to the delta (e.g. "vs prev 30d"). */
  deltaLabel?: string
  /** When true, a decrease is considered "good" (e.g. time-to-sell, stale count). Default false. */
  invertDelta?: boolean
  /** Domain-expert explanation of the metric (definition / formula / calculation). */
  info?: KPIInfo
}

const colorMap: Record<KPIColor, { bg: string; text: string; ring: string }> = {
  green: { bg: 'bg-green-100', text: 'text-green-600', ring: 'ring-green-200' },
  red: { bg: 'bg-red-100', text: 'text-red-600', ring: 'ring-red-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', ring: 'ring-blue-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', ring: 'ring-amber-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', ring: 'ring-purple-200' },
}

function computeDelta(value: number | string, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^\d.-]/g, ''))
  if (!Number.isFinite(numericValue) || !Number.isFinite(previous) || previous === 0) return null
  return ((numericValue - previous) / Math.abs(previous)) * 100
}

function DeltaChip({ pct, label, invert }: { pct: number | null; label?: string; invert?: boolean }) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null

  const rounded = Math.round(pct * 10) / 10
  const direction: 'up' | 'down' | 'flat' = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat'

  const isPositiveOutcome =
    direction === 'flat'
      ? null
      : invert
        ? direction === 'down'
        : direction === 'up'

  const color =
    isPositiveOutcome === null
      ? 'bg-secondary-100 text-secondary-500'
      : isPositiveOutcome
        ? 'bg-green-50 text-green-700'
        : 'bg-red-50 text-red-700'

  const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus

  return (
    <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${color} mt-1`}>
      <Icon className="h-3 w-3" />
      <span>
        {direction === 'flat' ? '0%' : `${rounded > 0 ? '+' : ''}${rounded}%`}
      </span>
      {label && <span className="text-secondary-500 font-normal ml-1">{label}</span>}
    </div>
  )
}

function InfoPopover({
  title,
  info,
  onClose,
}: {
  title: string
  info: KPIInfo
  onClose: () => void
}) {
  // Close on Escape key
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-secondary-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-secondary-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
            <p className="text-xs text-secondary-500">Metric definition & calculation</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-3 text-left text-sm">
          <section>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Definition
            </h4>
            <p className="text-secondary-800 leading-relaxed">{info.definition}</p>
          </section>
          {info.formula && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Formula
              </h4>
              <pre className="whitespace-pre-wrap rounded bg-secondary-50 px-3 py-2 font-mono text-xs text-secondary-800">
                {info.formula}
              </pre>
            </section>
          )}
          {info.calculation && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                How it's calculated
              </h4>
              <p className="text-secondary-800 leading-relaxed">{info.calculation}</p>
            </section>
          )}
          {info.source && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Source
              </h4>
              <p className="font-mono text-xs text-secondary-600">{info.source}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

export interface KPICardProps {
  card: KPICardData
  onClick?: () => void
}

export function KPICard({ card, onClick }: KPICardProps) {
  const colors = colorMap[card.color]
  const isClickable = !!onClick
  const deltaPct =
    card.deltaPct !== undefined && card.deltaPct !== null
      ? card.deltaPct
      : computeDelta(card.value, card.previousValue ?? null)

  const [infoOpen, setInfoOpen] = React.useState(false)

  return (
    <div
      className={`card relative text-center ${isClickable ? `cursor-pointer hover:ring-2 hover:${colors.ring} transition-shadow` : ''}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if ((e.key === 'Enter' || e.key === ' ') && onClick) onClick()
            }
          : undefined
      }
    >
      {card.info && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setInfoOpen(true)
          }}
          className="absolute top-2 right-2 rounded-full p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
          aria-label={`About ${card.label}`}
          title="What is this metric?"
        >
          <Info className="h-4 w-4" />
        </button>
      )}
      <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-3 ${colors.bg} rounded-full`}>
        <card.icon className={`h-6 w-6 ${colors.text}`} />
      </div>
      <p className="text-2xl font-bold text-secondary-900">{card.value}</p>
      <p className="text-sm text-secondary-500 mt-1">{card.label}</p>
      {card.detail && <p className="text-xs text-secondary-400 mt-1">{card.detail}</p>}
      <DeltaChip pct={deltaPct} label={card.deltaLabel} invert={card.invertDelta} />
      {infoOpen && card.info && (
        <InfoPopover title={card.label} info={card.info} onClose={() => setInfoOpen(false)} />
      )}
    </div>
  )
}

export function KPICardSkeleton() {
  return (
    <div className="card text-center">
      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 animate-pulse bg-secondary-200 rounded-full" />
      <div className="h-8 w-16 mx-auto animate-pulse bg-secondary-200 rounded mb-2" />
      <div className="h-4 w-24 mx-auto animate-pulse bg-secondary-200 rounded" />
    </div>
  )
}
